import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableSortLabel,
  TablePagination,
  Menu,
  FormControlLabel,
  Checkbox,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogContent,
  DialogActions,
  Divider,
  Autocomplete,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import CloseIcon from "@mui/icons-material/Close";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast, ToastContainer } from "react-toastify";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import {
  listCourses,
  createCourse,
  updateCourse,
  deleteCourse,
  apiErrorMessage,
} from "../../api/assessmentHierarchy";
import { COURSE_TYPES } from "./assessmentHierarchyData";
import useDebouncedValue from "./useDebouncedValue";
import AssessmentTableSkeleton from "./AssessmentTableSkeleton";
import AssessmentEmptyState from "./shell/AssessmentEmptyState";
const ACCENT = "#17479e";

const allColumns = [
  {
    key: "id",
    label: "ID",
    cellCss: { align: "center", minWidth: "70px" },
    getValue: (r) => r.id,
  },
  {
    key: "name",
    label: "Exam Name",
    cellCss: { minWidth: "220px" },
    getValue: (r) => r.name,
  },
  {
    key: "description",
    label: "Description",
    cellCss: { minWidth: "280px" },
    getValue: (r) => r.description,
  },
  {
    key: "type",
    label: "Type",
    cellCss: { minWidth: "140px" },
    getValue: (r) => r.type,
  },
  {
    key: "subjects",
    label: "Subjects",
    cellCss: { align: "center", minWidth: "110px" },
    getValue: (r) => r.subjectCount,
  },
  {
    key: "chapters",
    label: "Chapters",
    cellCss: { align: "center", minWidth: "110px" },
    getValue: (r) => r.chapterCount,
  },
  {
    key: "topics",
    label: "Topics",
    cellCss: { align: "center", minWidth: "110px" },
    getValue: (r) => r.topicCount,
  },
  {
    key: "subTopics",
    label: "Sub-Topics",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.subTopicCount,
  },
  {
    key: "videos",
    label: "Videos",
    cellCss: { align: "center", minWidth: "110px" },
    getValue: (r) => r.videoCount,
  },
  {
    key: "status",
    label: "Status",
    cellCss: { align: "center", minWidth: "110px" },
    getValue: (r) => r.status,
  },
];

const defaultVisibleKeys = new Set(allColumns.map((c) => c.key));
const STORAGE_KEY = "assessmentCoursesHiddenColumns";

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9" },
  INACTIVE: { label: "Inactive", color: "#555", bg: "#f0f0f0" },
};

function StatusChip({ value }) {
  const cfg = statusCfg[value] ?? {
    label: value ?? "—",
    color: "#555",
    bg: "#f5f5f5",
  };
  return (
    <Chip
      label={cfg.label}
      size="small"
      variant="outlined"
      sx={{
        fontWeight: 600,
        fontSize: "0.7rem",
        color: cfg.color,
        borderColor: cfg.color,
        backgroundColor: cfg.bg,
      }}
    />
  );
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2.5,
        py: 2,
        background: `linear-gradient(135deg, ${ACCENT}12 0%, ${ACCENT}03 100%)`,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: 38,
          height: 38,
          flexShrink: 0,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
          boxShadow: `0 4px 10px ${ACCENT}40`,
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography
          sx={{ fontWeight: 700, fontSize: "0.95rem", color: "#1a1a2e" }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: "0.72rem", color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

function CourseFormDialog({ open, course, onClose, onSave }) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");
  const [displayOrder, setDisplayOrder] = useState("");
  const [status, setStatus] = useState("ACTIVE");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      setSubmitting(false);
      return;
    }
    setName(course?.name || "");
    setType(course?.type || "");
    setDescription(course?.description || "");
    setDisplayOrder(course?.displayOrder ?? "");
    setStatus(course?.status || "ACTIVE");
  }, [open, course]);

  const isValid = name.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      await onSave({
        id: course?.id,
        name: name.trim(),
        type: type.trim() || null,
        description: description.trim(),
        displayOrder: displayOrder === "" ? undefined : Number(displayOrder),
        active: status === "ACTIVE",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { borderRadius: 3, backgroundColor: "#f7f8fb", overflow: "hidden" },
      }}
    >
      <Box
        sx={{
          position: "relative",
          p: 3,
          background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
          color: "#fff",
        }}
      >
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            color: "rgba(255,255,255,0.85)",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.18)",
            }}
          >
            <SchoolOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: "1.15rem" }}>
              {course ? "Edit Exam" : "Add New Exam"}
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", opacity: 0.85 }}>
              {course ? "Update exam details" : "Set up exam details"}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <DialogContent sx={{ pt: 3, backgroundColor: "#f7f8fb" }}>
        <Card sx={{ boxShadow: 1, borderRadius: 3, overflow: "hidden" }}>
          <SectionHeader
            icon={<InfoOutlinedIcon sx={{ fontSize: 20 }} />}
            title="Exam Details"
            subtitle="Name, type and description"
          />
          <CardContent sx={{ p: 3 }}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" },
                gap: 2,
              }}
            >
              <TextField
                label="Exam Name"
                fullWidth
                size="small"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Autocomplete
                freeSolo
                size="small"
                options={COURSE_TYPES}
                slotProps={{
                  popper: {
                    placement: "bottom-start",
                    style: { width: "fit-content", minWidth: 300 },
                  },
                  paper: { sx: { whiteSpace: "nowrap" } },
                }}
                ListboxProps={{ style: { maxHeight: 320 } }}
                inputValue={type}
                onInputChange={(_, value) => setType(value)}
                renderInput={(params) => <TextField {...params} label="Type" />}
              />
              <TextField
                label="Description"
                fullWidth
                size="small"
                multiline
                minRows={2}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                sx={{
                  gridColumn: { sm: "1 / -1" },
                  "& .MuiOutlinedInput-root": { borderRadius: 2 },
                }}
              />
              <Box
                sx={{
                  gridColumn: { sm: "1 / -1" },
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr" },
                  gap: 2,
                }}
              >
                <TextField
                  label="Display Order"
                  type="number"
                  fullWidth
                  size="small"
                  value={displayOrder}
                  onChange={(e) => setDisplayOrder(e.target.value)}
                  InputProps={{ inputProps: { min: 0 } }}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                />
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: 1.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: status === "ACTIVE" ? "#2e7d32" : "divider",
                backgroundColor: status === "ACTIVE" ? "#eaf7ec" : "#f5f7fb",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.85rem",
                    color: status === "ACTIVE" ? "#2e7d32" : "#555",
                  }}
                >
                  {status === "ACTIVE" ? "Active" : "Inactive"}
                </Typography>
                <Typography
                  sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                >
                  {status === "ACTIVE"
                    ? "Visible and usable for content and tests"
                    : "Hidden from students"}
                </Typography>
              </Box>
              <Switch
                checked={status === "ACTIVE"}
                onChange={(e) =>
                  setStatus(e.target.checked ? "ACTIVE" : "INACTIVE")
                }
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": { color: "#2e7d32" },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2e7d32",
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, backgroundColor: "#f7f8fb" }}>
        <Box sx={{ display: "flex", width: "100%", gap: 1.5 }}>
          <Button
            onClick={onClose}
            disabled={submitting}
            sx={{
              flex: "0 0 auto",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              color: "#555",
              backgroundColor: "#eceff1",
              "&:hover": { backgroundColor: "#e0e3e6" },
            }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            disableElevation
            loading={submitting}
            onClick={handleSubmit}
            disabled={!isValid}
            sx={{
              flex: 1,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${ACCENT} 0%, #1565c0 100%)`,
              "&:hover": {
                background: `linear-gradient(135deg, #123a80 0%, #0d47a1 100%)`,
              },
              "&.Mui-disabled": { background: "#b0bec5" },
            }}
          >
            {course ? "Save Changes" : "Create Exam"}
          </LoadingButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function AssessmentCourses() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search);
  const [statusFilter, setStatusFilter] = useState("");
  const [dense, setDense] = useState(true);
  const [orderBy, setOrderBy] = useState("name");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const hidden = JSON.parse(raw);
        if (Array.isArray(hidden)) {
          const visible = allColumns.map((c) => c.key).filter((k) => !hidden.includes(k));
          if (visible.length > 0) return new Set(visible);
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });
  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  const loadData = () => {
    setLoading(true);
    listCourses({ includeInactive: true })
      .then(setCourses)
      .catch((err) => {
        setCourses([]);
        toast.error(apiErrorMessage(err, "Failed to load exams"));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(allColumns.map((c) => c.key).filter((k) => !visibleColumns.has(k))),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));

  const filteredCourses = useMemo(() => {
    return courses.filter((c) => {
      if (
        debouncedSearch &&
        !c.name?.toLowerCase().includes(debouncedSearch.toLowerCase()) &&
        !c.description?.toLowerCase().includes(debouncedSearch.toLowerCase())
      )
        return false;
      if (statusFilter && c.status !== statusFilter) return false;
      return true;
    });
  }, [courses, debouncedSearch, statusFilter]);

  const sortedData = useMemo(() => {
    return [...filteredCourses].sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      if (aValue == null) return 1;
      if (bValue == null) return -1;
      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  }, [filteredCourses, orderBy, order]);

  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter]);

  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openAdd = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (course) => {
    setEditing(course);
    setFormOpen(true);
  };

  const handleSave = async (values) => {
    const { id, ...payload } = values;
    try {
      if (id) await updateCourse(id, payload);
      else await createCourse(payload);
    } catch (err) {
      toast.error(
        apiErrorMessage(
          err,
          id ? "Failed to update exam" : "Failed to create exam",
        ),
      );
      return;
    }
    toast.success(
      id ? "Exam updated successfully!" : "Exam created successfully!",
    );
    setFormOpen(false);
    loadData();
  };

  const handleDelete = () => {
    deleteCourse(deleteId)
      .then(() => {
        toast.success("Exam deactivated!");
        setDeleteId(null);
        loadData();
      })
      .catch((err) => {
        toast.error(apiErrorMessage(err, "Failed to delete exam"));
      });
  };

  return (
    <Box sx={{ textAlign: "left" }}>
      <ToastContainer />

      <Card sx={{ boxShadow: 1, mb: 2.5 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexWrap: "wrap",
                flex: "1 1 480px",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                placeholder="Search by exam name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                </Select>
              </FormControl>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ display: { xs: "none", md: "block" } }}
              >
                {sortedData.length} exams
              </Typography>
            </Box>

            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ flexShrink: 0 }}
            >
              <Tooltip title="Dense padding" arrow>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dense}
                      onChange={(e) => setDense(e.target.checked)}
                      color="primary"
                    />
                  }
                  label=""
                  sx={{ mr: 0 }}
                />
              </Tooltip>
              <Button
                variant="outlined"
                startIcon={<ViewColumnIcon />}
                onClick={handleOpenColumnsMenu}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Columns
              </Button>
              <Menu
                anchorEl={columnsMenuAnchor}
                open={openColumnsMenu}
                onClose={handleCloseColumnsMenu}
                PaperProps={{
                  sx: {
                    width: 200,
                    maxHeight: 500,
                    overflowY: "auto",
                    borderRadius: 2,
                  },
                }}
              >
                {allColumns.map((col) => (
                  <FormControlLabel
                    key={col.key}
                    control={
                      <Checkbox
                        checked={visibleColumns.has(col.key)}
                        onChange={() => toggleColumn(col.key)}
                      />
                    }
                    label={col.label}
                    sx={{ pl: 1, pr: 2 }}
                  />
                ))}
              </Menu>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={openAdd}
                {...BUTTON_STYLES.primary}
                sx={{
                  minWidth: { xs: "120px", md: "auto" },
                  fontSize: { xs: "10px", md: "12px" },
                }}
              >
                Add Exam
              </Button>
            </Stack>
          </Box>
        </CardContent>
      </Card>

      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: 2,
          borderRadius: 3,
        }}
      >
        <TableContainer sx={{ maxHeight: "calc(100vh - 220px)", minHeight: 420 }}>
          <Table stickyHeader size={dense ? "small" : "medium"}>
            <TableHead>
              <TableRow>
                {visibleColumnDefs.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.cellCss?.align}
                    sx={{
                      ...c.cellCss,
                      padding: "10px 12px",
                      backgroundColor: ACCENT,
                      color: "white",
                      fontWeight: 600,
                      position: "sticky",
                      top: 0,
                      zIndex: 3,
                    }}
                    sortDirection={orderBy === c.key ? order : false}
                  >
                    <TableSortLabel
                      active={orderBy === c.key}
                      direction={orderBy === c.key ? order : "asc"}
                      onClick={() => handleSort(c.key)}
                      sx={{
                        color: "#ffffff !important",
                        "&.Mui-active": { color: "#ffffff !important" },
                        "& .MuiTableSortLabel-icon": {
                          color: "#ffffff !important",
                        },
                      }}
                    >
                      {c.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell
                  align="center"
                  sx={{
                    minWidth: "110px",
                    padding: "10px 12px",
                    backgroundColor: ACCENT,
                    color: "white",
                    fontWeight: 600,
                    position: "sticky",
                    top: 0,
                    right: 0,
                    zIndex: 4,
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <AssessmentTableSkeleton
                  columns={visibleColumnDefs.length + 1}
                />
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumnDefs.length + 1}
                    sx={{ textAlign: "center", py: 4 }}
                  >
                    <AssessmentEmptyState message="No exams found" hint="Try changing your search or filters." />
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "rgba(0,0,0,0.02)",
                      },
                    }}
                  >
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.cellCss?.align}
                        sx={{ padding: "10px 12px", ...c.cellCss }}
                      >
                        {(() => {
                          const value = c.getValue(row);
                          if (c.key === "name") {
                            return (
                              <Typography
                                sx={{ fontWeight: 600, fontSize: "0.85rem" }}
                              >
                                {value}
                              </Typography>
                            );
                          }
                          if (c.key === "description") {
                            return (
                              <Typography
                                sx={{
                                  fontSize: "0.8rem",
                                  color: "text.secondary",
                                  maxWidth: 320,
                                  whiteSpace: "normal",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {value || "—"}
                              </Typography>
                            );
                          }
                          if (c.key === "subjects") return `${value} Subjects`;
                          if (c.key === "chapters") return `${value} Chapters`;
                          if (c.key === "topics") return `${value} Topics`;
                          if (c.key === "subTopics")
                            return `${value} Sub-Topics`;
                          if (c.key === "videos") return `${value} Videos`;
                          if (c.key === "status")
                            return <StatusChip value={value} />;
                          return value ?? "—";
                        })()}
                      </TableCell>
                    ))}
                    <TableCell
                      align="center"
                      sx={{
                        padding: "10px 12px",
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fff",
                        zIndex: 1,
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <Tooltip title="Edit Exam" arrow placement="top">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => openEdit(row)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "primary.light",
                                color: "white",
                              },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Exam" arrow placement="top">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => setDeleteId(row.id)}
                            sx={{
                              "&:hover": {
                                backgroundColor: "error.light",
                                color: "white",
                              },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            "& .MuiTablePagination-toolbar": {
              paddingLeft: 2,
              paddingRight: 2,
            },
          }}
        />
      </Paper>

      <CourseFormDialog
        open={formOpen}
        course={editing}
        onClose={() => setFormOpen(false)}
        onSave={handleSave}
      />

      <DeleteConfirmationDialog
        open={Boolean(deleteId)}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="This deactivates the exam (soft delete), hiding it from students. You can reactivate it later by editing the exam."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
}

export default AssessmentCourses;
